import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { var_api } from "../../../constant";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const ExpenseChart = () => {
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#db5014ff",
    "#9c27b0",
    "#e91e63",
    "#0be316ff",
  ];

  // ✅ Set default date range: 1st to 31st of current month
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // month is 0-indexed

    const formattedMonth = String(month).padStart(2, "0");
    const firstDay = `${year}-${formattedMonth}-01`;
    const lastDay = `${year}-${formattedMonth}-30`;

    setFromDate(firstDay);
    setToDate(lastDay);
  }, []);

  // ✅ Auto-fetch data once default dates are set
  useEffect(() => {
    if (fromDate && toDate) {
      handleGenerate();
    }
  }, [fromDate, toDate]);

  // ✅ Fetch expense data by date range
  const handleGenerate = async () => {
    if (!fromDate || !toDate) {
      toast.warn("Please select both From Date and To Date");
      return;
    }

    const formatDate = (dateStr) => {
      const [year, month, day] = dateStr.split("-");
      return `${day}-${month}-${year}`;
    };

    const formattedFrom = formatDate(fromDate);
    const formattedTo = formatDate(toDate);

    try {
      setLoading(true);
      const res = await axios.post(`${var_api}veemart_expenses/getbydaterange`, {
        from_date: formattedFrom,
        to_date: formattedTo,
      });

      if (res.data && Array.isArray(res.data.data)) {
        if (res.data.data.length === 0) {
          toast.info("No expenses found in this date range");
        }
        setData(res.data.data);
      } else {
        setData([]);
        toast.info("No data found in this date range");
      }
    } catch (error) {
      console.error("Error fetching date range data:", error);
      toast.error("Failed to fetch data for selected date range");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Search filter
  const searchedData = data.filter((item) =>
    item.expense_name
      ? item.expense_name.toLowerCase().includes(searchQuery.toLowerCase())
      : false
  );

  // ✅ Prepare Pie Chart data
  const pieData = searchedData.map((item) => ({
    name: item.expense_name || "N/A",
    value: Number(item.amount || 0),
  }));

  const totalAmount = pieData.reduce((sum, item) => sum + item.value, 0);

  return (
    <>
      <Card>
        <Card.Body>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Expense Chart</h4>
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: "250px" }}
              placeholder="Search expense..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Date Filters */}
          <Row className="align-items-end mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button
                variant="primary"
                className="w-100"
                onClick={handleGenerate}
                disabled={loading}
              >
                {loading ? "Loading..." : "Generate"}
              </Button>
            </Col>
          </Row>

          {/* Total */}
          <h5>
            Total Expense Amount:{" "}
            <span style={{ color: "blue" }}>₹ {totalAmount.toFixed(2)}</span>
          </h5>

          {/* Chart + Table */}
          <Row className="mt-4">
            {/* Chart */}
            <Col
              md={8}
              className="d-flex justify-content-center align-items-center"
            >
              {loading ? (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status"></div>
                  <div>Loading chart...</div>
                </div>
              ) : (
                <div style={{ width: "100%", height: 350 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="45%"
                        cy="50%"
                        outerRadius={120}
                        label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                      >
                        {pieData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val) => `₹ ${val}`} />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        wrapperStyle={{ marginLeft: "30px" }}
                        formatter={(value, entry) => {
                          const item = pieData.find((d) => d.name === value);
                          return (
                            <span
                              style={{
                                display: "inline-block",
                                marginBottom: "8px",
                              }}
                            >
                              <strong>{value}</strong> - ₹ {item?.value || 0}
                            </span>
                          );
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Col>

            {/* Table */}
            <Col md={4}>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Expense</th>
                      <th>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pieData.length === 0 ? (
                      <tr>
                        <td colSpan="2" className="text-center text-muted py-3">
                          No data found
                        </td>
                      </tr>
                    ) : (
                      <>
                        {pieData.map((row, index) => (
                          <tr key={index}>
                            <td>{row.name}</td>
                            <td>{row.value.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="table-success fw-bold">
                          <td>Total</td>
                          <td>₹ {totalAmount.toFixed(2)}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <ToastContainer />
    </>
  );
};

export default ExpenseChart;
